// firebase.js
import { initializeApp } from "firebase/app";
import {
  getAuth,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
} from "firebase/auth";
import {
  getDatabase,
  ref,
  set,
  get,
  push,
  child,
  update,
} from "firebase/database";

const firebaseConfig = {
  databaseURL:
    "https://tradeview-2957e-default-rtdb.europe-west1.firebasedatabase.app",
  apiKey: "AIzaSyBfU1Kh8cDzOhYPWbkoJbtbbJx7Q1LQBac",
  authDomain: "tradeview-2957e.firebaseapp.com",
  projectId: "tradeview-2957e",
  storageBucket: "tradeview-2957e.appspot.com",
  messagingSenderId: "368643923080",
  appId: "1:368643923080:web:6f39f1386fde59efe8533d",
  measurementId: "G-KDCZJQZB93",
};
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const database = getDatabase(app);

// Authentication functions
export const signInWithGoogle = async () => {
  const provider = new GoogleAuthProvider();
  try {
    const result = await signInWithPopup(auth, provider);
    return result.user;
  } catch (error) {
    console.error("Error signing in with Google", error);
    throw error;
  }
};

export const signOutUser = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error("Error signing out", error);
    throw error;
  }
};

// API Key management
export const saveApiKey = async (userId, apiKey) => {
  try {
    await set(ref(database, `users/${userId}/apiKey`), apiKey);
  } catch (error) {
    console.error("Error saving API key", error);
    throw error;
  }
};

export const getApiKey = async (userId) => {
  try {
    const snapshot = await get(ref(database, `users/${userId}/apiKey`));
    return snapshot.val();
  } catch (error) {
    console.error("Error getting API key", error);
    throw error;
  }
};

// Save Captial.com Auth
export const saveApiCredentials = async (userId, credentials) => {
  try {
    const userRef = ref(database, `users/${userId}/apiCredentials`);
    await set(userRef, credentials);
    console.log("API credentials saved successfully");
  } catch (error) {
    console.error("Error saving API credentials", error);
    throw error;
  }
};

export const getApiCredentials = async (userId) => {
  try {
    const snapshot = await get(ref(database, `users/${userId}/apiCredentials`));
    return snapshot.val();
  } catch (error) {
    console.error("Error getting API credentials", error);
    throw error;
  }
};



// Trade data functions
export const uploadTradeData = async (userId, parsedData) => {
  if (!userId || typeof userId !== "string") {
    throw new Error("Invalid user ID");
  }

  const tradesRef = ref(database, `users/${userId}/trades`);

  let added = 0;
  let skipped = 0;

  for (const transaction of parsedData) {
    if (!isValidDate(transaction.Modified)) {
      console.warn(
        `Skipping transaction with invalid date: ${transaction.Modified}`
      );
      skipped++;
      continue;
    }

    const date = new Date(transaction.Modified);
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();

    if (isNaN(year) || isNaN(month) || isNaN(day)) {
      console.warn(
        `Skipping transaction with invalid date components: ${transaction.Modified}`
      );
      skipped++;
      continue;
    }

    const dayRef = ref(
      database,
      `users/${userId}/trades/${year}/${month}/${day}`
    );
    const summaryRef = child(dayRef, "summary");
    const transactionsRef = child(dayRef, "transactions");

    // Check if transaction with this ID already exists
    const existingTransactionSnapshot = await get(transactionsRef);
    const existingTransactions = existingTransactionSnapshot.val() || {};
    const transactionExists = Object.values(existingTransactions).some(
      (t) => t.id === transaction.Id
    );

    if (transactionExists) {
      console.warn(`Skipping duplicate transaction with ID: ${transaction.Id}`);
      skipped++;
      continue;
    }

    // Update summary only for TRADE type
    if (transaction.Type === "TRADE") {
      const summarySnapshot = await get(summaryRef);
      const existingSummary = summarySnapshot.val() || { pl: 0, trades: 0 };
      const newSummary = {
        pl: existingSummary.pl + (parseFloat(transaction.Amount) || 0),
        trades: existingSummary.trades + 1,
      };
      await set(summaryRef, newSummary);
    }

    // Add new transaction
    const newTransactionRef = push(transactionsRef);
    await set(newTransactionRef, {
      id: transaction.Id || "",
      balance: parseFloat(transaction.Balance) || 0,
      amount: parseFloat(transaction.Amount) || 0,
      currency: transaction.Currency || "",
      type: transaction.Type || "",
      status: transaction.Status || "",
      modified: transaction.Modified || "",
      tradeId: transaction["Trade Id"] || "",
      instrumentSymbol: transaction["Instrument Symbol"] || "",
      instrumentName: transaction["Instrument Name"] || "",
      commission: transaction.Commission
        ? parseFloat(transaction.Commission)
        : null,
      accountType: transaction["Account type"] || "",
    });

    added++;
  }

  console.log(`Upload complete. Added: ${added}, Skipped: ${skipped}`);
  return { added, skipped };
};

export const fetchTradeData = async (userId, year, month) => {
  const monthRef = ref(database, `users/${userId}/trades/${year}/${month}`);
  try {
    const snapshot = await get(monthRef);
    if (snapshot.exists()) {
      const data = snapshot.val();
      const formattedData = {};
      for (const [day, dayData] of Object.entries(data)) {
        if (dayData.summary) {
          formattedData[day] = dayData.summary;
        }
      }
      return formattedData;
    } else {
      console.log("No data available for this month");
      return {};
    }
  } catch (error) {
    console.error("Error fetching trade data:", error);
    throw error;
  }
};

export const fetchDayDetails = async (userId, year, month, day) => {
  const dayRef = ref(
    database,
    `users/${userId}/trades/${year}/${month}/${day}`
  );
  try {
    const snapshot = await get(dayRef);
    if (snapshot.exists()) {
      console.log("Day details fetched successfully");
      return snapshot.val();
    } else {
      console.log("No data available for this day");
      return null;
    }
  } catch (error) {
    console.error("Error fetching day details:", error);
    throw error;
  }
};

export const fetchAvailableYears = async (userId) => {
  const yearsRef = ref(database, `users/${userId}/trades`);
  const snapshot = await get(yearsRef);
  if (snapshot.exists()) {
    return Object.keys(snapshot.val())
      .map(Number)
      .sort((a, b) => b - a); // Sort years in descending order
  } else {
    console.log("No years available");
    return [];
  }
};

export const updateTradeComment = async (userId, year, month, day, transactionId, comment) => {
  if (!userId) {
      throw new Error("User ID is required");
  }
  try {
      const transactionRef = ref(database, `users/${userId}/trades/${year}/${month}/${day}/transactions/${transactionId}`);
      await update(transactionRef, { comment: comment });
  } catch (error) {
      console.error("Error updating trade comment:", error);
      throw error;
  }
};
// Helper function
const isValidDate = (dateString) => {
  const timestamp = Date.parse(dateString);
  return !isNaN(timestamp);
};


export const updateDaySummary = async (userId, year, month, day, summary) => {
  if (!userId) {
    throw new Error("User ID is required");
  }
  try {
    const summaryRef = ref(database, `users/${userId}/trades/${year}/${month}/${day}/summary`);
    await set(summaryRef, summary);
    console.log("Day summary updated successfully");
  } catch (error) {
    console.error("Error updating day summary:", error);
    throw error;
  }
};

export { auth, database };
